import { camelizeKeys, decamelizeKeys } from 'humps';
import { API_URL } from '../../constants/branding';
import fetchJSON from '../fetchJSON';
import getAuthorizationHeaders from '../getAuthorizationHeaders';
import { getCookie } from '../manageCookie';
import { getV1Tokens, isHijackSession, refreshV1 } from '../authV1';

const DEFAULT_RETRIES_BY_METHOD = {
  get: 2,
  head: 2,
  options: 2,
};

const RETRY_BASE_DELAY = 500;

function isRetryableError(error) {
  if (!error) return false;
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return true;
  }
  if (error.isTimeout || error.status === 408) {
    return true;
  }
  if (
    error.isNetworkError ||
    error.status === 0 ||
    error.name === 'TypeError'
  ) {
    return true;
  }
  return false;
}

export default class Api {
  static async fetch(method, endpoint, options = {}) {
    try {
      let { data } = options;
      const {
        headers,
        notApiUrl,
        locale,
        isDecamelize = true,
        timeout,
        retries: _retries,
        retryDelay: _retryDelay,
        attempt: _attempt,
        ...opts
      } = options;
      let defaultHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      let body;
      if (locale) defaultHeaders['Accept-Language'] = locale;

      if (
        method === 'post' ||
        method === 'put' ||
        method === 'delete' ||
        method === 'PATCH'
      ) {
        const isFormData = data instanceof FormData;
        const isJSON = data instanceof Object && data.constructor === Object;

        if (isJSON) {
          if (isDecamelize) data = decamelizeKeys(data);
          body = JSON.stringify(data);
        }

        if (isFormData) {
          body = data;
          defaultHeaders = {};
        }

        defaultHeaders['X-CSRFToken'] = getCookie('csrftoken');
      }

      const url = notApiUrl ? endpoint : `${API_URL}/${endpoint}`;

      let json = await fetchJSON(url, {
        credentials: 'same-origin',
        headers: {
          ...defaultHeaders,
          ...getAuthorizationHeaders(),
          ...headers,
        },
        method,
        body,
        timeout,
        ...opts,
      });

      // TODO: check if all keys in undecore notation
      if (json instanceof Object && json.constructor === Object) {
        json = camelizeKeys(json);
      }

      return json;
    } catch (error) {
      const methodKey = (method || '').toLowerCase();
      let maxRetries = DEFAULT_RETRIES_BY_METHOD[methodKey] || 0;
      if (typeof options.retries === 'number') {
        maxRetries = options.retries;
      } else if (options.retry) {
        maxRetries = 2;
      }

      const attempt = options.attempt || 0;

      if (isRetryableError(error) && attempt < maxRetries) {
        const delay =
          typeof options.retryDelay === 'number'
            ? options.retryDelay
            : RETRY_BASE_DELAY * 2 ** attempt;
        await new Promise((resolve) => setTimeout(resolve, delay));
        return Api.fetch(method, endpoint, {
          ...options,
          attempt: attempt + 1,
        });
      }

      // On a v1 (JWT) session, a 401 means the access token expired — refresh once and retry.
      if (
        error &&
        error.status === 401 &&
        !options.retriedAfterRefresh &&
        !isHijackSession() &&
        getV1Tokens()
      ) {
        const refreshed = await refreshV1();
        if (refreshed) {
          return Api.fetch(method, endpoint, {
            ...options,
            retriedAfterRefresh: true,
          });
        }
      }
      return Promise.reject(error);
    }
  }

  static async get(endpoint, options) {
    return Api.fetch('get', endpoint, options);
  }

  static async patch(endpoint, options) {
    return Api.fetch('PATCH', endpoint, options);
  }

  static async post(endpoint, options) {
    return Api.fetch('post', endpoint, options);
  }

  static async put(endpoint, options) {
    return Api.fetch('put', endpoint, options);
  }

  static async delete(endpoint, options) {
    return Api.fetch('delete', endpoint, options);
  }

  static createXHR(method, endpoint) {
    const xhr = new XMLHttpRequest();
    xhr.open(method.toUpperCase(), `${API_URL}/${endpoint}`, true);
    const authHeaders = getAuthorizationHeaders();
    xhr.withCredentials = true;
    Object.keys(authHeaders).forEach((key) =>
      xhr.setRequestHeader(key, authHeaders[key])
    );
    return xhr;
  }
}
